import { useMemo, useState } from "react";

import { Button, Inline, InlineError, Select, Stack, Text } from "../../ui";

export interface ScenarioMemberOption {
  id: string;
  label: string;
}

export interface OrderedScenarioMembersEditorProps {
  error?: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onChange: (scenarioIds: string[]) => void;
  onLoadMore: () => void;
  options: ScenarioMemberOption[];
  value: string[];
}

export function OrderedScenarioMembersEditor({
  error,
  hasNextPage,
  isFetchingNextPage,
  onChange,
  onLoadMore,
  options,
  value,
}: OrderedScenarioMembersEditorProps) {
  const [candidateId, setCandidateId] = useState("");
  const optionById = useMemo(
    () => new Map(options.map((option) => [option.id, option])),
    [options],
  );
  const selectedIds = useMemo(() => new Set(value), [value]);
  const availableOptions = options.filter(
    (option) => !selectedIds.has(option.id),
  );

  function move(index: number, offset: -1 | 1) {
    const target = index + offset;
    if (target < 0 || target >= value.length) {
      return;
    }
    const reordered = [...value];
    const currentId = reordered[index];
    const targetId = reordered[target];
    if (currentId === undefined || targetId === undefined) {
      return;
    }
    reordered[index] = targetId;
    reordered[target] = currentId;
    onChange(reordered);
  }

  return (
    <Stack gap={3}>
      <Text>Scenarios run in the order shown.</Text>
      <Select
        label="Add scenario"
        value={candidateId}
        onChange={(event) => {
          setCandidateId(event.currentTarget.value);
        }}
      >
        <option value="">Select a scenario</option>
        {availableOptions.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </Select>
      <Inline>
        <Button
          disabled={candidateId.length === 0}
          type="button"
          variant="secondary"
          onClick={() => {
            if (candidateId.length === 0 || selectedIds.has(candidateId)) {
              return;
            }
            onChange([...value, candidateId]);
            setCandidateId("");
          }}
        >
          Add scenario
        </Button>
        {hasNextPage ? (
          <Button
            disabled={isFetchingNextPage}
            type="button"
            variant="ghost"
            onClick={onLoadMore}
          >
            {isFetchingNextPage ? "Loading..." : "Load more scenarios"}
          </Button>
        ) : null}
      </Inline>
      {value.length === 0 ? (
        <Text>No scenarios selected.</Text>
      ) : (
        <ol aria-label="Scenario order">
          {value.map((scenarioId, index) => {
            const label =
              optionById.get(scenarioId)?.label ?? `Scenario ${scenarioId}`;
            return (
              <li key={scenarioId}>
                <Inline justify="between">
                  <Text>{label}</Text>
                  <Inline gap={1}>
                    <Button
                      aria-label={`Move ${label} up`}
                      disabled={index === 0}
                      size="sm"
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        move(index, -1);
                      }}
                    >
                      Up
                    </Button>
                    <Button
                      aria-label={`Move ${label} down`}
                      disabled={index === value.length - 1}
                      size="sm"
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        move(index, 1);
                      }}
                    >
                      Down
                    </Button>
                    <Button
                      aria-label={`Remove ${label}`}
                      size="sm"
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        onChange(value.filter((id) => id !== scenarioId));
                      }}
                    >
                      Remove
                    </Button>
                  </Inline>
                </Inline>
              </li>
            );
          })}
        </ol>
      )}
      {error ? <InlineError>{error}</InlineError> : null}
    </Stack>
  );
}
